import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Separator } from '@/components/ui/separator'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { store } from '@/lib/states/store'
import { supabase } from '@/lib/supabase/supabaseClient'
import { trpc } from '@/lib/trpc/client'
import { trpcUtils } from '@/lib/trpc/vanilla'
import { applyOrgDtoToSession } from '@/lib/org/applyOrgDtoToSession'
import { errorToast } from '@/lib/utils/ToastUtils'
import { createFileRoute, Link } from '@tanstack/react-router'
import { useState } from 'react'
import { toast } from 'sonner'
import ImageUpload from '@/components/ImageUpload'
import LoadingSpinner from '@/components/LoadingSpinner'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { ColorPicker } from '@/components/ui/colour-picker'
import { useHasPermission } from '@/lib/states/session/hooks'
import { requirePermission } from '@/lib/permissions/permissions'
import { useOrgEntitlements } from '@/lib/hooks/useOrgEntitlements'
import {
  canUseBranding,
  canUsePublicIdentifier,
  PLAN_LABELS,
} from '@/lib/billing/plans'
import { Users, Zap } from 'lucide-react'

export const Route = createFileRoute(
  '/_auth/_dashboard/settings/_layout/organisation',
)({
  beforeLoad: () => {
    requirePermission('organisation.manage')
  },
  component: OrganisationPage,
  loader: async () => {
    const org = store.getState().session.org
    if (!org) {
      throw new Error('Org not found')
    }

    const profile = await trpcUtils.org.get.ensureData()
    return { org, imageUrl: profile.logo?.url ?? '' }
  },
})

function OrganisationPage() {
  const { org, imageUrl } = Route.useLoaderData()
  const canManageOrg = useHasPermission('organisation.manage')
  const { data: entitlements } = useOrgEntitlements()
  const allowBranding = canUseBranding(entitlements)
  const allowPublicIdentifier = canUsePublicIdentifier(entitlements)

  const [image, setImage] = useState<string>(imageUrl)
  const [imageFile, setImageFile] = useState<File>()
  const [loading, setLoading] = useState(false)
  const [orgName, setOrgName] = useState(org.name)
  const [orgSlug, setOrgSlug] = useState(org.slug ?? '')
  const [primaryColor, setPrimaryColor] = useState(org.primaryColor ?? '')
  const [logoVersion, setLogoVersion] = useState(org.logoVersion ?? 0)
  const [weightUnitDefault, setWeightUnitDefault] = useState<'kg' | 'lbs'>(
    org.weightUnitDefault ?? 'kg',
  )
  const [distanceUnitDefault, setDistanceUnitDefault] = useState<'km' | 'mi'>(
    org.distanceUnitDefault ?? 'km',
  )
  const [settingsSaving, setSettingsSaving] = useState(false)
  const [slugError, setSlugError] = useState<string | null>(null)
  const [logoPath] = useState(org.logoPath ?? `${org.id}/logo.png`)
  const updateProfile = trpc.org.updateProfile.useMutation()
  const createLogoUpload = trpc.org.createLogoUpload.useMutation()
  const utils = trpc.useUtils()
  const maxLogoSizeBytes = 2 * 1024 * 1024
  const normalizedPrimary = primaryColor.trim()
  const normalizeSlug = (value: string) =>
    value
      .toLowerCase()
      .trim()
      .replace(/\s+/g, '-')
      .replace(/[^a-z0-9-_]/g, '')
      .replace(/-+/g, '-')
  const initialSlug = org.slug ?? ''
  const initialPrimaryColor = (org.primaryColor ?? '').trim()
  const initialWeightUnitDefault = org.weightUnitDefault ?? 'kg'
  const initialDistanceUnitDefault = org.distanceUnitDefault ?? 'km'
  const normalizedSlug = normalizeSlug(orgSlug)
  const hasChanges =
    orgName.trim() !== org.name ||
    (allowPublicIdentifier && normalizedSlug !== initialSlug) ||
    (allowBranding && normalizedPrimary !== initialPrimaryColor) ||
    weightUnitDefault !== initialWeightUnitDefault ||
    distanceUnitDefault !== initialDistanceUnitDefault ||
    !!imageFile

  const onImageChanged = (file: File) => {
    if (file.size > maxLogoSizeBytes) {
      toast.error('Logo must be 2MB or smaller')
      return
    }
    setImage(URL.createObjectURL(file))
    setImageFile(file)
  }

  const handleSaveSettings = async () => {
    if (!orgName.trim()) {
      toast.error('Organisation name is required')
      return
    }
    if (
      allowBranding &&
      normalizedPrimary &&
      !/^#[0-9a-fA-F]{6}$/.test(normalizedPrimary)
    ) {
      toast.error('Primary colour must be a 6 digit hex value')
      return
    }
    if (
      allowPublicIdentifier &&
      normalizedSlug &&
      !/^[a-z0-9-_]+$/.test(normalizedSlug)
    ) {
      toast.error(
        'Slug can only include lowercase letters, numbers, hyphens, and underscores',
      )
      return
    }
    if (!hasChanges) {
      return
    }

    setSlugError(null)
    if (
      allowPublicIdentifier &&
      normalizedSlug &&
      normalizedSlug !== initialSlug
    ) {
      try {
        const { available } = await trpcUtils.org.checkSlugAvailable.ensureData(
          { slug: normalizedSlug },
        )
        if (!available) {
          const message = 'That public identifier is already in use.'
          setSlugError(message)
          toast.error(message, {
            description: 'Try a different slug so your public links are unique.',
          })
          return
        }
      } catch (error) {
        errorToast(error as Error)
        return
      }
    }

    setSettingsSaving(true)

    try {
      let nextLogoVersion = logoVersion
      let nextLogoPath = org.logoPath ?? logoPath

      if (imageFile) {
        setLoading(true)
        const signed = await createLogoUpload.mutateAsync()
        const { error: uploadError } = await supabase.storage
          .from(signed.bucket)
          .uploadToSignedUrl(signed.path, signed.token, imageFile)

        if (uploadError) {
          console.error(uploadError)
          errorToast(uploadError)
          return
        }

        nextLogoVersion = logoVersion + 1
        nextLogoPath = signed.path
      }

      const updated = await updateProfile.mutateAsync({
        name: orgName.trim(),
        weightUnitDefault,
        distanceUnitDefault,
        ...(allowBranding
          ? { primaryColor: normalizedPrimary || null }
          : {}),
        ...(allowPublicIdentifier
          ? { slug: normalizedSlug || null }
          : {}),
        ...(imageFile
          ? { logoPath: nextLogoPath, logoVersion: nextLogoVersion }
          : {}),
      })

      applyOrgDtoToSession(updated)
      utils.org.get.setData(undefined, () => updated)

      if (imageFile) {
        setLogoVersion(updated.logo_version)
        setImage(updated.logo?.url ?? image)
        setImageFile(undefined)
      }

      toast.success('Organisation settings updated')
    } catch (error) {
      console.error(error)
      if (isConflictError(error)) {
        const message = 'That public identifier is already in use.'
        setSlugError(message)
        toast.error(message, {
          description:
            'Try a different identifier so your public links are unique.',
        })
        return
      }
      errorToast(error as Error)
    } finally {
      setLoading(false)
      setSettingsSaving(false)
    }
  }

  return (
    <div className="space-y-6">
      {canManageOrg && (
        <>
          <div>
            <h3 className="text-lg font-medium">Organisation</h3>
            <p className="text-sm text-muted-foreground">
              Manage your organisation settings.
            </p>
            <div className="pt-3">
              <Button asChild variant="outline" size="sm">
                <Link to="/team" className="gap-2">
                  <Users className="h-4 w-4" />
                  Manage team
                </Link>
              </Button>
            </div>
          </div>
          <Separator />

          <div className="grid w-full max-w-sm items-center gap-2">
            <Label>Logo</Label>
            <ImageUpload image={image} onImageFileChanged={onImageChanged} />
          </div>

          <div className="space-y-3">
            <div className="space-y-2">
              <Label>Organisation name</Label>
              <Input
                value={orgName}
                onChange={(event) => setOrgName(event.target.value)}
                placeholder="Organisation name"
              />
            </div>
            <div className="space-y-2">
              <Label>Public Identifier</Label>
              <Input
                value={orgSlug}
                onChange={(event) => {
                  setSlugError(null)
                  setOrgSlug(event.target.value)
                }}
                onBlur={() => setOrgSlug(normalizeSlug(orgSlug))}
                placeholder="my-coaching"
                disabled={!allowPublicIdentifier}
              />
              <p className="text-xs text-muted-foreground">
                Used for public pages like forms.trainrr.app/
                {normalizedSlug || 'your-identifier'}
              </p>
              {slugError ? (
                <p className="text-xs text-destructive">{slugError}</p>
              ) : null}
            </div>
            <div className="space-y-2">
              <Label>Primary colour</Label>
              <div className="flex items-center gap-3">
                <ColorPicker
                  size={'icon'}
                  value={primaryColor || '#0051FF'}
                  onChange={(v) => setPrimaryColor(v as string)}
                  disabled={!allowBranding}
                />
                <Input
                  value={primaryColor}
                  onChange={(event) => setPrimaryColor(event.target.value)}
                  className="max-w-[200px]"
                  placeholder="#0051FF"
                  disabled={!allowBranding}
                />
                <Button
                  type="button"
                  variant="ghost"
                  className="h-10 px-2"
                  onClick={() => setPrimaryColor('')}
                  disabled={!primaryColor || !allowBranding}
                >
                  Clear
                </Button>
              </div>
            </div>
            {(!allowBranding || !allowPublicIdentifier) && (
              <Alert variant="info" className="mt-2">
                <Zap className="h-4 w-4" />
                <AlertTitle>Upgrade to unlock more branding</AlertTitle>
                <AlertDescription className="text-xs">
                  Upgrade to a higher plan to unlock custom branding
                  {entitlements?.required_plan_for_client_limit
                    ? ` (${PLAN_LABELS[entitlements.required_plan_for_client_limit]})`
                    : ''}
                  .{' '}
                  <Link
                    to="/upgrade"
                    search={{ plan: 'growth_monthly' }}
                    className="underline underline-offset-2"
                  >
                    View plans
                  </Link>
                </AlertDescription>
              </Alert>
            )}
          </div>

          <Separator />

          <div className="space-y-6">
            <div className="space-y-2">
              <Label>Default weight unit</Label>
              <Select
                value={weightUnitDefault}
                onValueChange={(value) =>
                  setWeightUnitDefault(value as 'kg' | 'lbs')
                }
              >
                <SelectTrigger className="w-[240px]">
                  <SelectValue placeholder="Select unit" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="kg">Kilograms (kg)</SelectItem>
                  <SelectItem value="lbs">Pounds (lbs)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Default distance unit</Label>
              <Select
                value={distanceUnitDefault}
                onValueChange={(value) =>
                  setDistanceUnitDefault(value as 'km' | 'mi')
                }
              >
                <SelectTrigger className="w-[240px]">
                  <SelectValue placeholder="Select unit" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="km">Kilometers (km)</SelectItem>
                  <SelectItem value="mi">Miles (mi)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <Button
              className="w-32"
              onClick={handleSaveSettings}
              disabled={settingsSaving || loading || !hasChanges}
            >
              Save {(settingsSaving || loading) && <LoadingSpinner />}
            </Button>
          </div>

          <Separator />
        </>
      )}
    </div>
  )
}

function isConflictError(error: unknown) {
  if (!error || typeof error !== 'object') {
    return false
  }
  const data =
    'data' in error
      ? (error as { data?: { code?: string } }).data
      : undefined
  const message =
    'message' in error ? String((error as { message?: string }).message) : ''
  return (
    data?.code === 'CONFLICT' ||
    message.includes('already in use') ||
    message.includes('organisations_slug_key')
  )
}
